import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Button } from '@mui/material';
import {
  TextInput,
  AutocompleteInput,
  useGetList,
  required,
  Form,
  CreateBase,
  useNotify,
  useDataProvider,
  useRefresh
} from 'react-admin';
import { Loader } from '../../Loader';

export const NewGameDialog = ({ open, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const { data: buildingData, isPending: isBuildingPending } = useGetList(
    'VegasBuilding',
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: 'createdAt', order: 'ASC' }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  );

  const handleSubmit = async (data) => {
    const transformedData = {
      ...data,
      type: 'VE',
      objectId: data.objectId ? data.objectId.trim() : undefined,
      slug: data.slug ? data.slug.trim() : undefined,
      gameURL: data.gameURL ? data.gameURL.trim() : undefined,
      RTP: data.RTP ? Number(data.RTP) : null
    };

    setLoading(true);
    try {
      const response = await dataProvider.create('GameCatalogue', {
        data: transformedData
      });

      if (response) {
        notify('Game created successfully!', { type: 'success' });
        handleClose();
        refresh();
      }
    } catch (error) {
      notify(error.message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      onClick={(evt) => evt.stopPropagation()}
    >
      <CreateBase resource="GameCatalogue">
        <Container component="overlay" maxWidth="sm" sx={{ p: 3 }}>
          {loading && <Loader />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              '& .MuiInputLabel-root': {
                textAlign: 'left',
                fontSize: '0.75em'
              },
              '& .MuiButton-root': { textTransform: 'capitalize' },
              '& .MuiGrid-item': {
                gap: 1
              },
              '& .MuiTextField-root': {
                flexBasis: '75%',
                alignItems: 'stretch'
              },
              '& .MuiAutocomplete-root': {
                flexBasis: '75%',
                alignItems: 'stretch'
              }
            }}
          >
            <Typography component="h1" variant="h6" sx={{ mb: 3 }}>
              Create New Game
            </Typography>
            <Form noValidate onSubmit={handleSubmit} sx={{ mt: 3, alignSelf: 'stretch' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextInput id="name" name="name" label="Name" fullWidth validate={[required()]} />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <AutocompleteInput
                    source="building"
                    label="Game Building"
                    choices={isBuildingPending ? [{ id: 'Null', name: 'Loading' }] : buildingData}
                    fullWidth
                    optionText="name"
                    optionValue="id"
                    validate={[required()]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextInput
                    id="gameSequence"
                    name="gameSequence"
                    label="Game Sequence"
                    fullWidth
                    validate={[required()]}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextInput
                    id="category"
                    name="category"
                    label="Category"
                    fullWidth
                    validate={[required()]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextInput id="slug" name="slug" label="Slug" fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    id="objectId"
                    name="objectId"
                    label="Object ID (optional)"
                    helperText="Leave empty to auto-generate. Set to keep the same ID across Dev/QA/Prod."
                    fullWidth
                    FormHelperTextProps={{ sx: { color: '#FFF' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput id="gameURL" name="gameURL" label="Game URL" fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <TextInput id="description" name="description" label="Description" fullWidth />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      border: '1px solid rgba(255, 239, 153, 0.20)',
                      color: '#FFF',
                      '&:hover': {
                        border: '1px solid #fff',
                        background: 'none'
                      }
                    }}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#FFA000'
                      }
                    }}
                  >
                    Confirm
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </Box>
        </Container>
      </CreateBase>
    </Dialog>
  );
};
